"use client";

import React from "react";

export interface TimelineItem {
  time: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  highlight?: boolean;
}

export interface TimelineSectionProps {
  title: string;
  subtitle?: string;
  items: TimelineItem[];
  orientation?: "horizontal" | "vertical";
  bgColor?: string;
}

export function TimelineSection({
  title,
  subtitle,
  items,
  orientation = "horizontal",
  bgColor = "bg-gray-50"
}: TimelineSectionProps) {
  if (orientation === "vertical") {
    return (
      <section className={`py-16 md:py-20 ${bgColor}`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {title}
            </h2>
            {subtitle && (
              <p className="text-xl text-gray-700">
                {subtitle}
              </p>
            )}
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>

            {items.map((item, index) => (
              <div key={index} className="relative flex items-start mb-8 last:mb-0">
                {/* Timeline dot */}
                <div className={`
                  absolute left-8 w-4 h-4 rounded-full -translate-x-1/2 mt-2
                  ${item.highlight ? 'bg-primary-600' : 'bg-gray-400'}
                `}></div>

                {/* Content */}
                <div className="ml-16">
                  <div className="text-sm text-gray-500 mb-1">{item.time}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.title}</h3>
                  {item.description && (
                    <p className="text-gray-700">{item.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Horizontal orientation
  return (
    <section className={`py-16 md:py-20 ${bgColor}`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          {subtitle && (
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              {subtitle}
            </p>
          )}
        </div>

        <div className="relative">
          {/* Horizontal line */}
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 hidden md:block"></div>

          <div className="grid md:grid-cols-5 gap-4 relative">
            {items.map((item, index) => (
              <div key={index} className="text-center relative">
                {/* Timeline dot */}
                <div className="flex justify-center mb-4">
                  <div className={`
                    w-12 h-12 rounded-full flex items-center justify-center z-10 relative
                    ${item.highlight 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-white border-2 border-gray-300 text-gray-600'
                    }
                  `}>
                    {item.icon || (
                      <span className="text-sm font-semibold">{index + 1}</span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div>
                  <div className="text-sm text-gray-500 mb-1">{item.time}</div>
                  <h3 className={`
                    text-sm font-semibold mb-1
                    ${item.highlight ? 'text-primary-600' : 'text-gray-900'}
                  `}>
                    {item.title}
                  </h3>
                  {item.description && (
                    <p className="text-xs text-gray-600">{item.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}